import type {
  KollectiveBaseLoadOptions,
  KollectiveContentTokenLoadOptions,
  KollectiveJitcLoadOptions,
  KollectiveLoadOptions,
} from '../../../@types/ksdk-api';
import type {
  HivePluginConfig,
  ImageConfig,
  KollectivePluginConfig,
  PlayerConfig,
  PluginsConfig,
  RampPluginConfig,
  SettingsConfig,
  VideoMetadataConfig,
  YouboraAnalyticsConfig,
} from '../player.config';
import type {
  AnalyticsMode,
  HivePluginOptions,
  PlayerOptions,
  PlayerSettings,
  Plugins,
  RampPluginOptions,
  VideoMetadata,
} from '../player.interface';

import { useEffect, useMemo } from 'react';
import { Logger } from 'tslog';
import { YouboraPluginConfig } from 'youboralib';

import { useLogger } from '../../../contexts/logger-context';
import { isInBetween } from '../../../utils/is-in-between';
import { isNumber } from '../../../utils/is-number';
import { isDebugEnabled } from '../../../utils/logger';
import { AudioCoverSource } from '../../audio-overlay/audio-overlay';
import { HiveSession } from '../p2p/hive.interface';
import { isKollectiveJitcOptions, isKollectiveRestOptions } from '../p2p/use-kollective';
import { PosterSource } from '../videojs-poster';

export function usePlayerOptions(config: PlayerConfig): PlayerOptions {
  const { logger: mainLogger } = useLogger();
  const logger = useMemo(() => mainLogger.getSubLogger({ name: 'usePlayerOptions' }), [mainLogger]);

  useEffect(() => {
    if (isDebugEnabled()) {
      logger.info('Player config:', config);
    }
  }, [config, logger]);

  const options: PlayerOptions = useMemo(() => {
    const analyticsMode = config.analyticsMode || 'minimal';

    const playerOptions: PlayerOptions = {
      lng: config.lng,
      poster: getPosterSource(config.poster, config.posters),
      sources: config.sources,
      metadata: getVideoMetadata(config.metadata),
      settings: getPlayerSettings(config.settings),
      tracks: config.tracks || [],
      storyboard: config.storyboard,
      chapters: config.chapters,
      relatedVideos: config.relatedVideos,
      onRelatedVideoClick: config.onRelatedVideoClick,
      plugins: getPlugins(logger, analyticsMode, config.plugins),
      onReady: config.onReady,
      watermark: config.watermark,
      analyticsMode,
      onOverlayVisibilityChange: config.onOverlayVisibilityChange,
      downloadSources: config.downloadSources || [],
      onTranscript: config.onTranscript,
      onTranscriptButtonClick: config.onTranscriptButtonClick,
      onFullscreenChange: config.onFullscreenChange,
      debug: config.debug,
      externalCues: config.externalCues,
    };

    logger.info('Player options:', playerOptions);

    return playerOptions;
  }, [
    logger,
    config.lng,
    config.poster,
    config.posters,
    config.sources,
    config.metadata,
    config.settings,
    config.tracks,
    config.storyboard,
    config.chapters,
    config.relatedVideos,
    config.onRelatedVideoClick,
    config.plugins,
    config.onReady,
    config.watermark,
    config.analyticsMode,
    config.onOverlayVisibilityChange,
    config.downloadSources,
    config.onTranscript,
    config.onTranscriptButtonClick,
    config.onFullscreenChange,
    config.debug,
    config.externalCues,
  ]);
  return options;
}

declare global {
  interface Window {
    hiveRenderStatsCallback?: (stats: unknown) => void;
    hiveOnActiveSession?: (session: HiveSession) => void;
    hiveOnErrorCallback?: (error: unknown) => void;
    hiveOnLoadingCompleted?: (plugin: unknown) => void;
  }
}

function getPosterSource(poster?: string, posters?: ImageConfig[]): PosterSource | undefined {
  return poster
    ? {
        src: poster,
        set:
          posters
            ?.sort((a, b) => b.width - a.width)
            .map((set) => ({
              src: set.source,
              width: set.width,
              height: set.height,
              mimeType: set.type,
              media: set.width ? `(min-width: ${set.width}px)` : '',
            })) || [],
      }
    : undefined;
}

function getAudioCoverSource(source?: string, sources?: ImageConfig[]): AudioCoverSource | undefined {
  return source
    ? {
        src: source,
        set:
          sources
            ?.sort((a, b) => b.width - a.width)
            .map((set) => ({
              src: set.source,
              media: set.width ? `(min-width: ${set.width}px)` : '',
            })) || [],
      }
    : undefined;
}

function getPlugins(logger: Logger<unknown>, analyticsMode: AnalyticsMode, config?: PluginsConfig): Plugins {
  return {
    hive: getHiveConfig(config?.hive),
    ramp: getVBrickConfig(config?.ramp),
    kollective: getKollectiveConfig(logger, analyticsMode, config?.kollective),
    youboraAnalytics: getYouboraOptions(config?.youboraAnalytics),
  };
}

// setSource: false, // We will handle the source setting

function getYouboraOptions(config?: YouboraAnalyticsConfig): YouboraPluginConfig | undefined {
  return config
    ? {
        ...config,
      }
    : undefined;
}

function getHiveConfig(config?: HivePluginConfig): HivePluginOptions | undefined {
  return config?.token || config?.ticket
    ? {
        ticket: config.ticket,
        token: config.token,
        eventName: config.eventName,
        techOrder: config.techOrder || ['HiveJava', 'HiveJS', 'StatsJS'],
        debugLevel: config.debugLevel || 'notice',
      }
    : undefined;
}

const defaultRampConfig: RampPluginConfig = {
  id: '',
  title: '',
  omnicacheUris: '',
  vbrickHostName: '',
  vbrickJwtKeyName: '',
  vbrickApiKey: '',
  vbrickApiSecret: '',
};

function getKollectiveConfig(
  logger: Logger<unknown>,
  analyticsMode: AnalyticsMode,
  config?: KollectivePluginConfig
): KollectiveLoadOptions | undefined {
  if (!config) {
    return undefined;
  }

  const baseOptions: KollectiveBaseLoadOptions = {
    userToken: analyticsMode === 'full' ? config.userToken : 'Unknown',
    setSource: false, // We handle the source setting
    createObjectId: (url) => {
      try {
        const result = cleanupSourceUrl(url);

        logger.info('Kollective createObjectId result before:', url);
        logger.info('Kollective createObjectId result after:', result);

        return result;
      } catch (error) {
        logger.error('Kollective createObjectId failed:', error);
        return url;
      }
    },
  };

  if (isKollectiveJitcOptions(config)) {
    const result: KollectiveJitcLoadOptions = {
      ...baseOptions,
      integratorToken: config.integratorToken,
      jitc: config.jitc,
    };
    return result;
  }

  if (isKollectiveRestOptions(config)) {
    const result: KollectiveContentTokenLoadOptions = {
      ...baseOptions,
      contentToken: config.contentToken,
    };

    return result;
  }

  logger.error('Unsupported Kollective configuration. Returning undefined.');
  return undefined;
}

function getVBrickConfig({
  id,
  title = '',
  omnicacheUris = '',
  vbrickHostName = '',
  vbrickJwtKeyName = '',
  vbrickApiKey = '',
  vbrickApiSecret = '',
}: RampPluginConfig = defaultRampConfig): RampPluginOptions | undefined {
  const p2pEnabled = !!vbrickHostName && !!vbrickJwtKeyName && !!vbrickApiKey && !!vbrickApiSecret;

  return omnicacheUris || p2pEnabled
    ? {
        id,
        title,
        vbrickHostName,
        vbrickJwtKeyName,
        vbrickApiKey,
        vbrickApiSecret,
        omnicacheUris,
        p2pEnabled,
      }
    : undefined;
}

function getVideoMetadata(config?: VideoMetadataConfig): VideoMetadata {
  return {
    videoId: config?.videoId || '',
    title: config?.title || '',
    description: config?.description || '',
  };
}

function getPlayerSettings(settings?: SettingsConfig): PlayerSettings {
  return {
    primaryColor: settings?.primaryColor || '',
    theme: settings?.theme,
    controlBarColor: settings?.controlBarColor || '',
    controlBarOpacity: getColorbarOpacity(settings?.controlBarOpacity),
    iconColor: settings?.iconColor || '',
    bigPlayButtonIconColor: settings?.bigPlayButtonIconColor || '',
    logoUrl: settings?.logoUrl || '',
    responsive: settings?.responsive ?? true,
    autoplay: settings?.autoplay ?? false,
    looping: settings?.looping ?? false,
    muted: settings?.muted ?? false,
    liveUI: settings?.liveUI ?? false,
    noUI: settings?.noUI ?? false,
    audioOnly: settings?.audioOnly ?? false,
    audioCoverSource: getAudioCoverSource(settings?.audioOnlyImageUrl, settings?.audioOnlyImageUrls),
    volume: settings?.volume ?? 0.7,
    trimmable: settings?.trimmable ?? false,
    hideLogo: settings?.hideLogo ?? false,
    hideTitle: settings?.hideTitle ?? false,
    hidePlayButton: settings?.hidePlayButton ?? false,
    hideTimeline: settings?.hideTimeline ?? false,
    hideVolumeInput: settings?.hideVolumeInput ?? false,
    hideTimer: settings?.hideTimer ?? false,
    hideLoopButton: settings?.hideLoopButton ?? false,
    hideMenu: settings?.hideMenu ?? false,
    hideFullscreenButton: settings?.hideFullscreenButton ?? false,
    hidePlaybackSpeed: settings?.hidePlaybackSpeed ?? false,
    hideSettingsMenu: settings?.hideSettingsMenu ?? false,
    hideLanguageMenu: settings?.hideLanguageMenu ?? false,
    hideBigPlayButton: settings?.hideBigPlayButton ?? false,
    hideChaptersButton: settings?.hideChaptersButton ?? false,
    hideTranscriptButton: settings?.hideTranscriptButton ?? false,
    hideTranscriptDownload: settings?.hideTranscriptDownload ?? false,
    stepButtonsAreVisible: settings?.stepButtonsAreVisible ?? false,
    preferredQuality: settings?.preferredQuality || '',
    maxPlaylistRetries: settings?.maxPlaylistRetries ?? 15,
    aspectRatio: getVideoJsAspectRatio(settings?.aspectRatio),
    videoLayout: settings?.videoLayout || 'fluid',
    breakpoints: settings?.breakpoints || [],
    subtitleEnabled: settings?.subtitleEnabled ?? false,
    subtitleFallbackLanguage: settings?.subtitleFallbackLanguage || 'en',
    preloadSources: settings?.preloadSources || 'auto',
    hideControlsBeforePlay: settings?.hideControlsBeforePlay ?? false,
    mediaSessionEnabled: settings?.mediaSessionEnabled ?? false,
  };
}

function getVideoJsAspectRatio(aspectRatio: '16:9' | '4:3' | 'Auto' | undefined): '16:9' | '4:3' | undefined {
  /*
   * The "Auto" option is there for backwards compatibilty with VMPRO options.
   * What we call "Auto" is the default behavior for video.js. So there's no need to set it.
   */
  return aspectRatio === '16:9' || aspectRatio === '4:3' ? aspectRatio : undefined;
}

function getColorbarOpacity(controlBarOpacity: number | undefined): number {
  if (!isNumber(controlBarOpacity) || !isInBetween(controlBarOpacity, 0, 1)) {
    return 0;
  }

  return controlBarOpacity;
}

const ksdkParameterRegEx = /hdntl=[^/]*\//;
const ksdkFileRegEx = /(\.m3u8|\.ts)(\?.*)?$/;

/**
 * Cleans up a given source URL by removing unnecessary query parameters and extracting the main file path.
 *
 * This function is typically used to process URLs for video streams, such as `.m3u8` or `.ts` files.
 *
 * @param {string} url - The source URL to be cleaned. Expected to be a valid HTTPS URL.
 * @returns {string} - The cleaned URL, containing only the main file path (e.g., `.m3u8` or `.ts`), or the original URL if no match is found.
 *
 * @example
 * // Example 1: URL with hdntl parameter
 * cleanupSourceUrl('https://example.com/hdntl=somevalue/video.m3u8');
 * // Returns: 'https://example.com/video.m3u8'
 *
 * @example
 * // Example 2: URL with query parameters
 * cleanupSourceUrl('https://example.com/video.m3u8?param=value');
 * // Returns:      'https://example.com/video.m3u8'
 *
 */
export function cleanupSourceUrl(url: string): string {
  if (!url) return url;
  // Remove hdntl parameter and query string, keep only main file path (.m3u8 or .ts)
  return url.replace(ksdkParameterRegEx, '').replace(ksdkFileRegEx, '$1');
}
